import React, { useState } from "react";
import { useSelector } from "react-redux";
import { RootState } from "../store";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCaretDown,
  faSquare,
  faSquareCheck,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import BackButton from "./BackButton";
import { RefundReason, Transaction, TipDetail } from "../types";
import * as S from "./Refund.styles";
import {
  useGetLocationTransactionsQuery,
  useGetGuestDetailsQuery,
  useCreateRefundMutation,
} from "../services/managersApi";

interface RefundProps {
  refundReasons?: RefundReason[];
}

function calculateRefundBreakdown(transaction: Transaction) {
  const refundableAmount = parseFloat(transaction.amount);
  let totalTips = 0;
  const deductions: { amount: number; label: string; fee_type: string }[] = [];

  if (transaction.tip_details) {
    transaction.tip_details.forEach((detail: TipDetail) => {
      totalTips += detail.amount;
      deductions.push({
        amount: detail.amount,
        label: detail.label,
        fee_type: detail.fee_type,
      });
    });
  }

  const grandTotal = refundableAmount + totalTips;

  return {
    refundableAmount,
    grandTotal,
    deductions,
  };
}

const Refund: React.FC<RefundProps> = ({ refundReasons = [] }) => {
  const navigate = useNavigate();
  const { toBeRefunded } = useSelector((state: RootState) => state.checkout);
  const selectedLocation = useSelector(
    (state: RootState) => state.checkout.selectedLocation,
  );
  const selectedGuest = useSelector(
    (state: RootState) => state.checkout.selectedGuest,
  );

  const locationId = selectedLocation?.id?.toString() ?? "";
  const guestId = selectedGuest?.id?.toString() ?? "";

  const { refetch: refetchLocationTransactions } =
    useGetLocationTransactionsQuery(locationId, { skip: !locationId });
  const { refetch: refetchGuestDetails } = useGetGuestDetailsQuery(
    { userId: guestId, locationId: locationId },
    {
      skip: !guestId || !locationId,
    },
  );
  const [createRefund] = useCreateRefundMutation();

  const [reasonDropdownOpen, setReasonDropdownOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string>("");
  const [refundAmount, setRefundAmount] = useState<string>("");
  const [refundNotes, setRefundNotes] = useState<string>("");
  const [adjustTip, setAdjustTip] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);

  const formatCurrency = (inputValue: string): string => {
    if (inputValue.trim() === "") return "";

    const numberValue = parseInt(inputValue, 10);
    const dollars = Math.floor(numberValue / 100);
    const cents = numberValue % 100;

    return `$${dollars}.${cents.toString().padStart(2, "0")}`;
  };

  const handleRefundAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const numericValue = value.replace(/\D/g, "");

    if (numericValue === "") {
      setRefundAmount("$0.00");
      return;
    }

    const formattedValue = formatCurrency(numericValue);
    setRefundAmount(formattedValue);
  };

  if (!toBeRefunded) {
    return (
      <S.Container>
        <BackButton onClick={() => navigate(-1)} />
        <div>No refund data available</div>
      </S.Container>
    );
  }

  const breakdown = calculateRefundBreakdown(toBeRefunded);
  const refundAmountValue = refundAmount
    ? parseFloat(refundAmount.replace(/[$,]/g, ""))
    : 0;
  const maxRefundAmount = breakdown.refundableAmount;
  const isAmountValid =
    refundAmountValue > 0 && refundAmountValue <= maxRefundAmount;
  const isFormValid = selectedReason && refundAmount && isAmountValid;

  const handleSubmit = () => {
    if (!isFormValid || isSubmitting) return;
    setShowConfirmDialog(true);
  };

  const handleConfirmRefund = async () => {
    setIsSubmitting(true);
    setError("");
    setShowConfirmDialog(false);

    try {
      await createRefund({
        credit_transaction_id: toBeRefunded.id,
        refund_reason: selectedReason,
        refund_amount: refundAmountValue,
        refund_notes: refundNotes,
        modify_tip: adjustTip,
      }).unwrap();

      // Navigate back or show success message
      refetchLocationTransactions();
      if (guestId) {
        refetchGuestDetails();
      }
      navigate(-1);
    } catch (err: any) {
      const errorMessage =
        err?.data?.errors?.join(", ") ||
        err?.data?.error ||
        "Failed to process refund";
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseDialog = () => {
    setShowConfirmDialog(false);
  };

  return (
    <S.Container>
      <BackButton onClick={() => navigate(-1)} />

      <S.RefundableSection>
        <S.SectionHeader>
          <S.SectionTitle>Issue a Refund</S.SectionTitle>
          <S.Divider />
        </S.SectionHeader>

        <S.RefundableAmountContainer>
          <S.AmountRow>
            <S.AmountLabel>Refundable Amount</S.AmountLabel>
            <S.AmountValue>
              ${breakdown.refundableAmount.toFixed(2)}
            </S.AmountValue>
          </S.AmountRow>
          {breakdown.deductions.map((deduction) => (
            <S.AmountRow key={deduction.fee_type}>
              <S.AmountSubLabel>{deduction.label}</S.AmountSubLabel>
              <S.AmountSubValue>
                ${deduction.amount.toFixed(2)}
              </S.AmountSubValue>
            </S.AmountRow>
          ))}
        </S.RefundableAmountContainer>
      </S.RefundableSection>

      <S.FormField>
        <S.Label>Reason for Refund*</S.Label>
        <S.DropdownContainer>
          <S.DropdownButton
            onClick={() => setReasonDropdownOpen(!reasonDropdownOpen)}
          >
            {selectedReason
              ? refundReasons.find((r) => r.value === selectedReason)?.label ||
                selectedReason
              : "Select"}
            <FontAwesomeIcon icon={faCaretDown} />
          </S.DropdownButton>
          <S.DropdownOptions isOpen={reasonDropdownOpen}>
            {refundReasons.map((reason) => (
              <S.DropdownOption
                key={reason.value}
                onClick={() => {
                  setSelectedReason(reason.value);
                  setReasonDropdownOpen(false);
                }}
              >
                {reason.label}
              </S.DropdownOption>
            ))}
          </S.DropdownOptions>
        </S.DropdownContainer>
      </S.FormField>

      <S.FormField>
        <S.Label>Refund Amount*</S.Label>
        <S.Input
          type="text"
          value={refundAmount}
          onChange={handleRefundAmountChange}
          placeholder="$0.00"
        />
        {!isAmountValid && refundAmountValue > 0 && (
          <div style={{ color: "#ff4444", fontSize: "12px", marginTop: "4px" }}>
            Refund amount cannot exceed refundable amount of $
            {maxRefundAmount.toFixed(2)}
          </div>
        )}
      </S.FormField>

      <S.FormField>
        <S.Label>
          Notes <S.OptionalLabel>(Optional)</S.OptionalLabel>
        </S.Label>
        <S.TextArea
          value={refundNotes}
          onChange={(e) => setRefundNotes(e.target.value)}
          placeholder="Optional notes about the refund..."
        />
      </S.FormField>

      <S.CheckboxContainer>
        <S.CheckboxRow>
          <S.CheckboxIcon onClick={() => setAdjustTip(!adjustTip)}>
            <FontAwesomeIcon icon={adjustTip ? faSquareCheck : faSquare} />
          </S.CheckboxIcon>
          <S.CheckboxLabel onClick={() => setAdjustTip(!adjustTip)}>
            Adjust tip for partial refund?
          </S.CheckboxLabel>
        </S.CheckboxRow>
        <S.NoteText>
          Note: Full refunds always include the tip amount
        </S.NoteText>
      </S.CheckboxContainer>

      {error && (
        <div
          style={{
            color: "#ff4444",
            fontSize: "14px",
            padding: "12px",
            background: "#2c2c2c",
            border: "1px solid #ff4444",
            borderRadius: "8px",
          }}
        >
          {error}
        </div>
      )}

      <S.SubmitButton
        disabled={!isFormValid || isSubmitting}
        onClick={handleSubmit}
      >
        {isSubmitting ? "Processing..." : "Issue Refund"}
      </S.SubmitButton>

      {showConfirmDialog && (
        <S.ModalOverlay>
          <S.ModalContent>
            <S.ModalHeader>
              <S.ModalTitle>Are you sure?</S.ModalTitle>
              <S.ModalCloseButton onClick={handleCloseDialog}>
                <FontAwesomeIcon icon={faXmark} />
              </S.ModalCloseButton>
            </S.ModalHeader>
            <S.ModalDescription>
              This action will return funds to the guest's original payment
              method.
            </S.ModalDescription>
            <S.ModalButton onClick={handleConfirmRefund}>
              Confirm Refund
            </S.ModalButton>
          </S.ModalContent>
        </S.ModalOverlay>
      )}
    </S.Container>
  );
};

export default Refund;
